import { action, computed, makeObservable, observable, override } from 'mobx';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import * as Api from '../../sdk';
import { FilteredPageCollectionController, IBaseObservablePageCollectionControllerOptions } from '../Collections';
import { UserSessionContext, ViewModel } from '../index';

export const getApiActionPathForSurvey = (survey: Api.ISurvey) => {
	switch (survey._type) {
		case 'SatisfactionSurvey': {
			return 'satisfaction';
		}
		case 'EventRegistrationSurvey': {
			return 'eventRegistration';
		}
		default: {
			return '';
		}
	}
};

const toViewModel = (
	userSession: UserSessionContext,
	survey: Api.ISurvey,
	impersonationContext?: Api.IImpersonationContext
) => {
	switch (survey._type) {
		case 'SatisfactionSurvey': {
			return new SatisfactionSurveyViewModel(userSession, survey as Api.ISatisfactionSurvey).impersonate(
				impersonationContext
			);
		}
		case 'EventRegistrationSurvey': {
			return new EventSurveyViewModel(userSession, survey as Api.IEventRegistrationSurvey).impersonate(
				impersonationContext
			);
		}
		default: {
			return new SurveyViewModel(userSession, survey).impersonate(impersonationContext);
		}
	}
};

export class SurveyViewModel<TSurvey extends Api.ISurvey = Api.ISurvey> extends ViewModel {
	protected mSurvey: TSurvey;

	public static init<TSurvey extends Api.ISurvey = Api.ISurvey>(
		userSession: UserSessionContext,
		survey: TSurvey,
		impersonationContext?: Api.IImpersonationContext
	) {
		if (survey._type === 'SatisfactionSurvey') {
			/**
			 * HACK to add missing fields to OLD satisfaction surveys
			 * @TODO - We will need to do something similar when migrating to use a react query to fetch the survey
			 */
			const _survey = survey as unknown as Api.ISatisfactionSurvey;
			const fieldId = uuid();
			if (!_survey.customForm?.fields?.length) {
				_survey.customForm = {
					name: _survey.name,
					fields: [
						{
							fieldType: Api.FormFieldType.String,
							id: fieldId,
							label: '',
							isHidden: false,
							isOptional: true,
						},
					],
				};
			}
			if (!_survey?.customFormMetaData) {
				_survey.customFormMetaData = [
					{
						fieldId,
						isConditional: false,
						ratings: [1, 2, 3],
					},
				];
			}
			return toViewModel(userSession, _survey, impersonationContext);
		}
		return toViewModel(userSession, survey, impersonationContext);
	}

	public static create = async <TSurveyViewModel extends SurveyViewModel>(
		userSession: UserSessionContext,
		survey: Api.ISurvey,
		impersonationContext?: Api.IImpersonationContext
	): Promise<TSurveyViewModel> => {
		const opResult = await userSession.webServiceHelper.callWebServiceAsync<Api.ISurvey>(
			Api.ImpersonationBroker.composeApiUrl({
				impersonationContext,
				queryParams: null,
				urlPath: `survey/${getApiActionPathForSurvey(survey)}`,
			}),
			'POST',
			survey
		);

		if (!opResult.success) {
			throw opResult;
		}

		return toViewModel(userSession, opResult.value, impersonationContext) as TSurveyViewModel;
	};

	public static load = async <TSurveyViewModel extends SurveyViewModel>(
		userSession: UserSessionContext,
		survey: Api.ISurvey,
		impersonationContext?: Api.IImpersonationContext
	) => {
		const opResult = await userSession.webServiceHelper.callWebServiceAsync<Api.ISurvey>(
			Api.ImpersonationBroker.composeApiUrl({
				impersonationContext,
				urlPath: `survey/${survey?.id}`,
				queryParams: { expand: 'options' },
			}),
			'GET'
		);

		if (!opResult.success) {
			throw opResult;
		}

		return toViewModel(userSession, opResult.value, impersonationContext) as TSurveyViewModel;
	};

	constructor(userSession: UserSessionContext, survey: TSurvey) {
		super(userSession);

		makeObservable<SurveyViewModel<TSurvey>, 'mSurvey'>(this, {
			mSurvey: observable.ref,
			type: computed,
			id: computed,
			creationMoment: computed,
			archiveMoment: computed,
			anonymousLink: computed,
			creator: computed,
			name: computed,
			expirationMoment: computed,
			startMoment: computed,
			customForm: computed,
		});

		this.executeRequest = this.executeRequest.bind(this);
		this.mSetSurvey = this.mSetSurvey.bind(this);
		this.mSetSurvey(survey);
	}

	public get customForm() {
		switch (this.mSurvey._type) {
			case 'SatisfactionSurvey': {
				return (this.mSurvey as any as Api.ISatisfactionSurvey)?.customForm;
			}
			case 'EventRegistrationSurvey': {
				return (this.mSurvey as any as Api.IEventRegistrationSurvey)?.customForm;
			}
			default: {
				return null;
			}
		}
	}

	public get type() {
		return this.mSurvey?._type;
	}

	public get id() {
		return this.mSurvey?.id;
	}

	public get creationMoment() {
		return this.mSurvey?.creationDate ? moment(this.mSurvey.creationDate) : null;
	}

	public get archiveMoment() {
		return this.mSurvey?.archivedDate ? moment(this.mSurvey.archivedDate) : null;
	}

	public get anonymousLink() {
		return this.mSurvey?.anonymousLink;
	}

	public get creator() {
		return this.mSurvey?.creator;
	}

	public get name() {
		return this.mSurvey?.name;
	}

	public get expirationMoment() {
		return this.mSurvey?.expirationDate ? moment(this.mSurvey.expirationDate) : null;
	}

	public get startMoment() {
		return this.mSurvey?.startDate ? moment(this.mSurvey.startDate) : null;
	}

	public load = async () => {
		if (!this.busy) {
			this.busy = true;
			const opResult = await this.executeRequest('GET');
			this.busy = false;
			if (!opResult.success) {
				throw opResult;
			}

			this.mSetSurvey(opResult.value);
			return opResult;
		}
	};

	public archive = async () => {
		if (!this.busy) {
			this.busy = true;
			const opResult = await this.executeRequest('DELETE');
			this.busy = false;

			if (!opResult.success) {
				throw opResult;
			}

			this.mSetSurvey(opResult.value);
			return opResult;
		}
	};

	public update = async (survey: TSurvey) => {
		if (survey && !this.busy) {
			this.busy = true;

			const opResult = await this.executeRequest('PUT', null, null, survey);
			this.busy = false;

			if (!opResult.success) {
				throw opResult;
			}

			this.mSetSurvey(opResult.value);
			return opResult;
		}
	};

	public getLink = async () => {
		if (!this.busy) {
			this.busy = true;
			const opResult = await this.executeRequest<string>('GET', 'link');
			this.busy = false;

			if (!opResult.success) {
				throw opResult;
			}
			return opResult;
		}
	};

	public toJs = () => {
		return this.mSurvey;
	};

	protected mSetSurvey(survey?: TSurvey) {
		this.mSurvey = survey;
	}

	protected executeRequest<TResult = TSurvey>(
		method: Api.HTTPMethod,
		navigationPath?: string,
		queryParams?: Api.IDictionary<any>,
		survey?: TSurvey
	) {
		return this.userSession.webServiceHelper.callWebServiceAsync<TResult>(
			this.composeApiUrl({
				queryParams,
				urlPath: `survey/${this.mSurvey.id}${navigationPath ? `/${navigationPath}` : ''}`,
			}),
			method,
			survey
		);
	}
}

export class SatisfactionSurveyViewModel extends SurveyViewModel<Api.ISatisfactionSurvey> {
	constructor(userSession: UserSessionContext, survey: Api.ISatisfactionSurvey) {
		super(userSession, survey);

		makeObservable(this, {
			reviewRequestSettings: computed,
			stats: computed,
			companyDisplayName: computed,
			intro: computed,
		});
	}

	public get reviewRequestSettings() {
		return this.mSurvey?.reviewRequestSettings;
	}

	public get stats() {
		return this.mSurvey?.stats;
	}

	public get companyDisplayName() {
		return this.mSurvey?.companyDisplayName;
	}

	public get intro() {
		return this.mSurvey?.intro;
	}
}

export class EventSurveyViewModel extends SurveyViewModel<Api.IEventRegistrationSurvey> {
	constructor(userSession: UserSessionContext, survey: Api.IEventRegistrationSurvey) {
		super(userSession, survey);

		makeObservable(this, {
			stats: computed,
			startMoment: override,
			eventInformation: computed,
			attendeeOptions: computed,
		});
	}

	public get stats() {
		return this.mSurvey?.stats;
	}

	public get startMoment() {
		return this.mSurvey?.eventInformation?.startTime ? moment(this.mSurvey.eventInformation.startTime) : null;
	}

	public get eventInformation() {
		return this.mSurvey?.eventInformation;
	}

	public get attendeeOptions() {
		return this.mSurvey?.attendeeOptions;
	}
}

export class SurveyReportViewModel<
	TSurvey extends Api.ISurvey = Api.ISurvey,
	TSurveyResponse extends Api.ISurveyResponse = Api.ISurveyResponse,
	TSurveyViewModel extends SurveyViewModel<TSurvey> = SurveyViewModel<TSurvey>,
	TSortDescriptor extends Api.ISortDescriptor = Api.ISortDescriptor,
	TResponseTypeDescriptor extends Api.IResponseTypeDescriptor = Api.IResponseTypeDescriptor,
> extends FilteredPageCollectionController<TSurveyResponse, TSurveyResponse, Api.ISurveyResponseFilterRequest> {
	protected mSurvey: TSurveyViewModel;
	public dateRange: { start: Date; end: Date };
	public sortDescriptor: TSortDescriptor;
	public responseTypeDescriptor: TResponseTypeDescriptor;

	public static instanceWithSurvey = <
		STSurvey extends Api.ISurvey = Api.ISurvey,
		STSurveyResponse extends Api.ISurveyResponse = Api.ISurveyResponse,
		STSurveyViewModel extends SurveyViewModel<STSurvey> = SurveyViewModel<STSurvey>,
	>(
		userSession: UserSessionContext,
		survey: STSurveyViewModel,
		options?: Partial<IBaseObservablePageCollectionControllerOptions<STSurveyResponse, STSurveyResponse>>
	) => {
		if (survey instanceof SatisfactionSurveyViewModel) {
			return new SatisfactionSurveyReportViewModel(userSession, survey, options as any).impersonate(
				survey.impersonationContext
			);
		}
		if (survey instanceof EventSurveyViewModel) {
			return new EventSurveyReportViewModel(userSession, survey, options as any).impersonate(
				survey.impersonationContext
			);
		}

		// @ts-ignore
		return new SurveyReportViewModel(userSession, survey, options).impersonate(survey.impersonationContext);
	};

	constructor(
		userSession: UserSessionContext,
		survey: TSurveyViewModel,
		options?: Partial<IBaseObservablePageCollectionControllerOptions<TSurveyResponse, TSurveyResponse>>
	) {
		super({
			apiPath: () => {
				return `survey/${this.mSurvey?.id}/response/filter`;
			},
			client: userSession.webServiceHelper,
			...(options || {}),
		});

		makeObservable(this, {
			dateRange: observable.ref,
			sortDescriptor: observable.ref,
			responseTypeDescriptor: observable.ref,
			request: computed,
			getNext: override,
			export: action,
			getStatsForDateRange: action,
		});

		this.getNext = this.getNext.bind(this);
		this.export = this.export.bind(this);
		this.mSurvey = survey;
		this.dateRange = {
			end: moment().endOf('month').toDate(),
			start: moment().subtract(1, 'month').startOf('month').toDate(),
		};
	}

	public get request(): Api.ISurveyResponseFilterRequest {
		const request: Api.ISurveyResponseFilterRequest = {
			endDate: this.dateRange?.end?.toISOString(),
			startDate: this.dateRange?.start?.toISOString(),

			surveyId: this.mSurvey?.id,
		};

		return request;
	}

	public getNext(request = this.request, pageSize?: number, params?: Api.IDictionary) {
		const computedParams = this.sortDescriptor ? { ...(params || {}), ...this.sortDescriptor } : null;

		return super.getNext(request, pageSize, computedParams);
	}

	public async export(request = this.request) {
		const requestBody: Api.ISurveyResponseExportRequest = {
			filter: request,

			surveyId: this.mSurvey.id,
		};

		const opResult = await this.mSurvey.userSession.webServiceHelper.callWebServiceAsync<Api.ISystemJob>(
			this.composeApiUrl({ urlPath: `survey/${getApiActionPathForSurvey(this.mSurvey.toJs())}/response/export` }),
			'POST',
			requestBody
		);

		if (!opResult.success) {
			throw opResult;
		}

		return opResult.value;
	}

	public get survey() {
		return this.mSurvey;
	}

	public load = () => {
		return this.mSurvey?.load();
	};

	public setDateRangeToShowAllResponses = () => {
		const nextDateRange = {
			end: moment(this.mSurvey.expirationMoment || moment())
				.endOf('month')
				.toDate(),
			start: moment(this.mSurvey.startMoment || this.mSurvey.creationMoment)
				.startOf('month')
				.toDate(),
		};
		this.dateRange = nextDateRange;
	};

	public getStatsForDateRange = async (dateRange?: { start?: Date; end?: Date }) => {
		const opResult = await this.mSurvey.userSession.webServiceHelper.callWebServiceAsync<Api.ISatisfactionSurveyStats>(
			this.composeApiUrl({
				urlPath: `survey/${getApiActionPathForSurvey(this.mSurvey.toJs())}/${this.mSurvey.id}/stats/filter`,
			}),
			'POST',
			dateRange
				? {
						...this.request,
						endDate: dateRange.end?.toISOString(),
						startDate: dateRange.start?.toISOString(),
					}
				: this.request
		);
		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	};
}

export class SatisfactionSurveyReportViewModel extends SurveyReportViewModel<
	Api.ISatisfactionSurvey,
	Api.ISatisfactionSurveyResponse,
	SatisfactionSurveyViewModel,
	Api.ISatisfactionSurveySortDecriptor
> {
	constructor(
		userSession: UserSessionContext,
		survey: SatisfactionSurveyViewModel,
		options?: Partial<
			IBaseObservablePageCollectionControllerOptions<Api.ISatisfactionSurveyResponse, Api.ISatisfactionSurveyResponse>
		>
	) {
		super(userSession, survey, {
			apiPath: () => {
				return `survey/satisfaction/response/filter`;
			},
			client: userSession.webServiceHelper,
			...(options || {}),
		});
		makeObservable(this, {
			request: override,
		});
	}

	public get request(): Api.ISatisfactionSurveyResponseFilterRequest {
		const request: Api.ISatisfactionSurveyResponseFilterRequest = {
			endDate: this.dateRange?.end?.toISOString(),
			startDate: this.dateRange?.start?.toISOString(),

			surveyId: this.mSurvey?.id,
			_type: 'SatisfactionSurveyResponseFilterRequest',
		};

		return request;
	}
}

export class EventSurveyReportViewModel extends SurveyReportViewModel<
	Api.IEventRegistrationSurvey,
	Api.IEventRegistrationSurveyResponse,
	EventSurveyViewModel,
	Api.IEventSurveySortDescriptor,
	Api.IEventSurveyResponseTypeDescriptor
> {
	public selectedStatus: Api.EventRegistrationResponseStatus = Api.EventRegistrationResponseStatus.Attending;
	public eventResponseStats: Api.IEventRegistrationSurveyResponseStats;

	public eventResponseSearchQuery = '';

	constructor(
		userSession: UserSessionContext,
		survey: EventSurveyViewModel,
		options?: Partial<
			IBaseObservablePageCollectionControllerOptions<
				Api.IEventRegistrationSurveyResponse,
				Api.IEventRegistrationSurveyResponse
			>
		>
	) {
		super(userSession, survey, {
			apiPath: () => {
				return `survey/eventRegistration/response/filter`;
			},
			client: userSession.webServiceHelper,
			...(options || {}),
		});

		makeObservable(this, {
			selectedStatus: observable,
			eventResponseStats: observable.ref,
			eventResponseSearchQuery: observable.ref,
			searchQuery: computed,
			request: override,
			export: override,
		});

		this.eventResponseStats = survey.stats?.responseStats?.find(x => x.category === 'Total');
	}

	public get searchQuery() {
		return this.eventResponseSearchQuery;
	}
	public set searchQuery(value: string) {
		this.eventResponseSearchQuery = value;
	}

	public get request() {
		const request: Api.IEventRegistrationSurveyResponseFilterRequest = {
			status: [this.selectedStatus],
			search: this.eventResponseSearchQuery,
			surveyId: this.survey?.id,
			_type: 'EventRegistrationSurveyResponseFilterRequest',
		};

		return request;
	}

	public async export(request = this.request) {
		const exportRequest = { ...(request || {}) } as Api.IEventRegistrationSurveyResponseFilterRequest;

		delete exportRequest.status; // important, we want to export all
		return super.export(exportRequest);
	}
}
